import * as THREE from "three";
import MathUtils from "./MathUtils";
import { deepEqual } from "./Misc";
import ScopeCamera from "../Camera";
import DefaultSceneManager from "../DefaultSceneManager";
import MaterialAsset from "../MaterialAsset";
import Text3D from "../Text3D";
import AudioSource from "../AudioSource";
import { SaveModificationTypes } from "../../globals";
import ScopeLight from "../Light";

export const ComparisonFunctions = {
	[SaveModificationTypes.Position]: (object: THREE.Object3D, defaultObject: THREE.Object3D) => MathUtils.areVectorsEqual(object.position, defaultObject.position),
	[SaveModificationTypes.Rotation]: (object: THREE.Object3D, defaultObject: THREE.Object3D) => MathUtils.areVectorsEqual(object.rotation, defaultObject.rotation),
	[SaveModificationTypes.Scale]: (object: THREE.Object3D, defaultObject: THREE.Object3D) => MathUtils.areVectorsEqual(object.scale, defaultObject.scale),
	[SaveModificationTypes.Name]: (object: THREE.Object3D, defaultObject: THREE.Object3D) => object.name === defaultObject.name,
	[SaveModificationTypes.Visibility]: (object: THREE.Object3D, defaultObject: THREE.Object3D) => compareVisibility(object, defaultObject),
	[SaveModificationTypes.Groups]: (object: THREE.Object3D, defaultObject: THREE.Object3D) => compareGroups(object, defaultObject),
	[SaveModificationTypes.Material]: (object: THREE.Object3D, defaultObject: THREE.Object3D) => compareObjectMaterials(object, defaultObject),
	[SaveModificationTypes.Text3D]: (object: THREE.Object3D, defaultObject: THREE.Object3D) => compareText3D(object as Text3D, defaultObject as Text3D),
	[SaveModificationTypes.Camera]: (object: THREE.Object3D, defaultObject: THREE.Object3D) => compareCamera(object as ScopeCamera, defaultObject as ScopeCamera),
	[SaveModificationTypes.Light]: (object: THREE.Object3D, defaultObject: THREE.Object3D) => compareLight(object as ScopeLight, defaultObject as ScopeLight),
	[SaveModificationTypes.Audio]: (object: THREE.Object3D, defaultObject: THREE.Object3D) => compareAudio(object, defaultObject),
	[SaveModificationTypes.Billboard]: (object: THREE.Object3D, defaultObject: THREE.Object3D) => compareBillboards(object, defaultObject),
	[SaveModificationTypes.Callout]: (object: THREE.Object3D, defaultObject: THREE.Object3D) => compareCallouts(object, defaultObject),
};

/**
 * Updates the active modifications for an object, copying the save modifications to the active modifications and then checking if each modification has changed
 * 
 * @export
 * @param {THREE.Object3D} object The object to update the active modifications for
 * @param {(THREE.Object3D | null)} [defaultObject=null] The default object to compare to (can be null, default object will be retrieved from the default scene manager)
 * @return {*} 
 */
export function updateObjectActiveModifications(
	object: THREE.Object3D, defaultObject: THREE.Object3D | null = null
)
{
	defaultObject = defaultObject ?? getDefaultObject(object);
	if (!defaultObject) return;
	if (!object.userData.saveModifications) 
	{
		object.userData.saveModifications = []; 
		return;
	}
	object.userData.activeModifications = [...object.userData.saveModifications];
	const modifications = object.userData.activeModifications;
	const activeModifications: SaveModificationTypes[] = [];
	modifications.forEach((modification) => 
	{
		if (!checkModification(object, modification, defaultObject)) 
		{
			activeModifications.push(modification);
		}
	});

	object.userData.activeModifications = activeModifications;
}

/**
 * Checks if a modification has changed for an object
 * 
 * @export
 * @param {THREE.Object3D} object The object to check the modification for
 * @param {SaveModificationTypes} modification The modification to check
 * @param {(THREE.Object3D | null)} [defaultObject=null] The default object to compare to. Default object will be retrieved from the default scene manager if not provided
 * @return {boolean} True if the comparison finds no changes (marking equal to the default object), otherwise false
 */
export function checkModification(object: THREE.Object3D, modification: SaveModificationTypes, defaultObject: THREE.Object3D | null = null): boolean
{
	if (!ComparisonFunctions[modification]) return true;
	defaultObject = defaultObject ?? getDefaultObject(object);
	if (!defaultObject) return true;
	return Boolean(ComparisonFunctions[modification]?.(object, defaultObject));
}

// Visibility is stored as an object with keys for each visibility mode
// We need to compare the values of each key
// However, sometimes it places keys in a different order, and sometimes it places keys with undefined values
function compareVisibility(
	object: THREE.Object3D,
	defaultObject: THREE.Object3D
): boolean
{
	const objectVisibility = Object.values(object.userData.visibility).filter((value) => value !== undefined).sort();
	const defaultVisibility = Object.values(defaultObject.userData.visibility).filter((value) => value !== undefined).sort();
	for (let i = 0; i < objectVisibility.length; i++)
	{
		if (objectVisibility[i] !== defaultVisibility[i]) return false;
	}
	return true;
}

// Groups are stored as an array of strings
// We need to compare the values of each string
// Sometimes the default object has no groups and that can either be an empty array or undefined
function compareGroups(
	object: THREE.Object3D,
	defaultObject: THREE.Object3D
): boolean 
{
	const objectGroups = object.userData.groups;
	const defaultGroups = defaultObject.userData.groups;
	if (objectGroups === defaultGroups) return true;
	if (defaultGroups === undefined && objectGroups?.length === 0) return true;
	if (defaultGroups === undefined && objectGroups?.length > 0) return false;
	if (defaultGroups?.length !== objectGroups?.length) return false;
	for (const group of objectGroups)
	{
		if (!defaultGroups.includes(group)) return false;
	}
	return true;
}

// Billboards and Callouts are stored as objects with keys for each property
// Sometimes if the default object has no billboard or callout, it will be undefined
function compareCallouts(
	object: THREE.Object3D,
	defaultObject: THREE.Object3D
): boolean 
{
	if (!defaultObject.userData.annotation && !object.userData.annotation?.enabled) return true;
	return deepEqual(object.userData.annotation, defaultObject.userData.annotation);
}

function compareBillboards(
	object: THREE.Object3D,
	defaultObject: THREE.Object3D
): boolean 
{
	if (!defaultObject.userData.billboard && !object.userData.billboard?.enabled) return true;
	return deepEqual(object.userData.billboard, defaultObject.userData.billboard);
}

function compareObjectMaterials(
	object: THREE.Object3D,
	defaultObject: THREE.Object3D
): boolean 
{
	const material = MaterialAsset.getMaterialAssetByObjectGuid(
		object.userData.guid
	);
	const defaultMaterial = MaterialAsset.getMaterialAssetByObjectGuid(
		defaultObject.userData.guid
	);
	return material?.guid === defaultMaterial?.guid;
}

function getDefaultObject(object: THREE.Object3D): THREE.Object3D | null 
{
	return DefaultSceneManager.getInstance().getDefaultObject(
		object.userData.guid
	);
}

function compareText3D(
	object: Text3D,
	defaultObject: Text3D | null = null
): boolean 
{
	if (!(object instanceof Text3D)) return true;
	const defaultText3D = defaultObject ?? (getDefaultObject(object) as Text3D);
	if (object.getTextColor() !== defaultText3D.getTextColor()) return false;
	if (object.getText() !== defaultText3D.getText()) return false;
	if (object.getTextAnchor() !== defaultText3D.getTextAnchor()) return false;
	return true;
}

function compareCamera(
	object: ScopeCamera,
	defaultObject: ScopeCamera | null = null
): boolean 
{
	if (!(object instanceof ScopeCamera)) return true;
	const defaultCamera =
		(defaultObject as ScopeCamera) ??
		(getDefaultObject(object) as ScopeCamera);
	if (object.getCameraNearPlane() !== defaultCamera.getCameraNearPlane())
		return false;
	if (object.getCameraFarPlane() !== defaultCamera.getCameraFarPlane())
		return false;
	if (object.getCameraFov() !== defaultCamera.getCameraFov()) return false;
	if (
		object.getCameraOrthographicSize() !==
		defaultCamera.getCameraOrthographicSize()
	)
		return false;
	if (object.getCameraAspect() !== defaultCamera.getCameraAspect())
		return false;
	if (
		object.getCameraIsInteractiveMode() !==
		defaultCamera.getCameraIsInteractiveMode()
	)
		return false;
	if (
		object.getCameraProjectionMode() !==
		defaultCamera.getCameraProjectionMode()
	)
		return false;
	return true;
}

function compareLight(
	object: ScopeLight,
	defaultObject: ScopeLight | null = null
): boolean 
{
	if (!(object instanceof ScopeLight)) return true;
	const defaultLight =
		defaultObject ?? (getDefaultObject(object) as ScopeLight);
	if (object.getLightColor() !== defaultLight.getLightColor()) return false;
	if (object.getLightIntensity() !== defaultLight.getLightIntensity())
		return false;
	return true;
}

function compareAudio(
	object: AudioSource,
	defaultObject: AudioSource | null = null
): boolean 
{
	if (!(object instanceof AudioSource)) return true;
	const defaultAudio =
		defaultObject ?? (getDefaultObject(object) as AudioSource);
	if (object.getAudio3D() !== defaultAudio.getAudio3D()) return false;
	if (object.getAudioVolume() !== defaultAudio.getAudioVolume()) return false;
	if (object.getAudioClip() !== defaultAudio.getAudioClip()) return false;
	if (object.getAudioRange() !== defaultAudio.getAudioRange()) return false;
	return true;
}
